import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Article, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
  } catch {
    return s;
  }
}

const ACTION_COLORS = {
  "auth.login": "text-green-700 bg-green-50",
  "note.restore": "text-blue-700 bg-blue-50",
  "note.hard_delete": "text-red-700 bg-red-50",
  "note.empty_trash": "text-red-700 bg-red-50",
  "backup.create": "text-amber-700 bg-amber-50",
  "backup.delete": "text-red-700 bg-red-50",
  "backup.restore": "text-purple-700 bg-purple-50",
  "backup.auto_create": "text-amber-600 bg-amber-50",
  "backup.config": "text-ink/70 bg-black/5",
  "logs.clear": "text-red-700 bg-red-50",
  "db.baseline_set": "text-ink/70 bg-black/5",
};

export default function LogsDialog({ open, onOpenChange }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/logs?limit=500`);
      setLogs(data);
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const clear = async () => {
    if (!window.confirm("ΔΙΑΓΡΑΦΗ ΟΛΩΝ ΤΩΝ LOGS;")) return;
    try {
      await axios.delete(`${API}/admin/logs`);
      toast.success("ΔΙΑΓΡΑΦΗΚΑΝ");
      load();
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[760px] rounded-2xl p-0 overflow-hidden border-none max-h-[92vh] overflow-y-auto"
        data-testid="logs-dialog"
      >
        <div className="px-7 pt-6 pb-3 bg-ink text-white">
          <DialogHeader>
            <DialogTitle className="font-caveat text-3xl font-bold flex items-center gap-2">
              <Article size={24} weight="bold" />
              LOGS
            </DialogTitle>
            <DialogDescription className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold">
              ΕΝΕΡΓΕΙΕΣ ΣΤΟ ΣΥΣΤΗΜΑ
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 py-5 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70">
              {logs.length} ΕΓΓΡΑΦΕΣ (νεότερα πρώτα)
            </div>
            {logs.length > 0 && (
              <button
                onClick={clear}
                data-testid="logs-clear"
                className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Trash size={12} weight="bold" />
                ΔΙΑΓΡΑΦΗ
              </button>
            )}
          </div>

          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {loading && <p className="text-xs text-ink/50 italic">ΦΟΡΤΩΣΗ...</p>}
            {!loading && logs.length === 0 && (
              <p className="text-xs text-ink/50 italic">ΧΩΡΙΣ ΕΓΓΡΑΦΕΣ</p>
            )}
            {logs.map((l) => (
              <div
                key={l.id}
                data-testid={`log-row-${l.id}`}
                className="grid grid-cols-[100px_minmax(120px,1fr)_120px_1fr] gap-3 items-center px-3 py-2 rounded-lg bg-white border border-black/5 hover:bg-black/3 transition-colors text-[11px] font-arial"
              >
                <span className="text-ink/50 tabular-nums">{fmt(l.ts)}</span>
                <span className="font-bold text-ink uppercase tracking-wide truncate">
                  {l.actor || "—"}
                </span>
                <span
                  className={`uppercase tracking-wider font-black text-[9px] px-1.5 py-0.5 rounded text-center ${
                    ACTION_COLORS[l.action] || "text-ink/70 bg-black/5"
                  }`}
                >
                  {l.action}
                </span>
                <span className="text-ink/65 truncate" title={l.detail}>
                  {l.detail || ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
